import { useCallback, useEffect, useState } from "react";
import { getShell } from "../lib/apiKey";

/**
 * Theme preference + the "native chrome" flag for app shells.
 *
 * Dark is the default. Light is opt-in and only applies on routes that
 * have been migrated to the design tokens (see LIGHT_READY_ROUTES); every
 * other route keeps the dark canvas its components were written for.
 */
export type ThemePref = "dark" | "light";

const PREF_KEY = "ev_theme";
const CHROME_KEY = "ev_chrome";
const EVENT = "ev-theme-change";

/** Routes whose components read colours from tokens (safe in light). */
const LIGHT_READY = [/^\/$/, /^\/pricing\/?$/, /^\/molecules\/?$/, /^\/molecule\//, /^\/tools\/?$/, /^\/(login|register)\/?$/, /^\/(blog|article)\/?$/,
  /^\/(molar-mass|balance|reaction|stoichiometry|acid-base|quiz|config-builder|periodic-table|quantum-orbitals|self-ionization)\/?$/,
  /^\/(docs|license|privacy|cookies|terms|support|dev|extensions|download|download-web|file-upload)\/?$/,
  /^\/(credits|membership|membership-electron)\/?$/, /^\/shop(\/|$)/, /^\/admin\/shop\/?$/];

export function isLightReadyRoute(pathname: string): boolean {
  return LIGHT_READY.some((re) => re.test(pathname));
}

export function getThemePref(): ThemePref {
  try {
    return localStorage.getItem(PREF_KEY) === "light" ? "light" : "dark";
  } catch {
    return "dark";
  }
}

export function setThemePref(p: ThemePref) {
  try {
    localStorage.setItem(PREF_KEY, p);
  } catch {}
  window.dispatchEvent(new CustomEvent(EVENT, { detail: p }));
}

export function useThemePref(): [ThemePref, (p: ThemePref) => void] {
  const [pref, setPref] = useState<ThemePref>(getThemePref);
  useEffect(() => {
    const on = () => setPref(getThemePref());
    window.addEventListener(EVENT, on);
    return () => window.removeEventListener(EVENT, on);
  }, []);
  const set = useCallback((p: ThemePref) => setThemePref(p), []);
  return [pref, set];
}

/**
 * Applies `data-theme` on <html> for the current route. Electronify's
 * light mode inverts the whole page with a CSS filter, so it always gets
 * the dark tokens (the filter then turns them light).
 */
export function useApplyTheme(pathname: string) {
  const [pref] = useThemePref();
  useEffect(() => {
    const forceDark = getShell() === "electronify" || !isLightReadyRoute(pathname);
    document.documentElement.setAttribute("data-theme", forceDark ? "dark" : pref);
  }, [pref, pathname]);
}

/**
 * `?chrome=native` is sent by app builds that draw their own top bar and
 * tab bar (Atomizer AR 3.x). Persisted per session like `?shell=`.
 */
export function hasNativeChrome(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const q = new URLSearchParams(window.location.search).get("chrome");
    if (q === "native") sessionStorage.setItem(CHROME_KEY, "native");
    return sessionStorage.getItem(CHROME_KEY) === "native";
  } catch {
    return false;
  }
}

/** Body classes for shell-specific CSS in tokens.css. */
export function useShellBodyClasses() {
  useEffect(() => {
    const shell = getShell();
    const cls: string[] = [];
    if (shell) cls.push("shell-native", `shell-${shell}`);
    if (shell && hasNativeChrome()) cls.push("chrome-native");
    document.body.classList.add(...cls);
    return () => document.body.classList.remove(...cls);
  }, []);
}
